import React, { useState, useEffect } from "react";

import { formatName, abbreviateNumber } from "helpers/miscHelpers";

import MultiLineChart from "./CoronaChart";
import { StyledUI, StyledOptionsMenu, StyledGraphHolder } from "./styles";

const CoronaGraphs = (props) => {
  const {
    chartType,
    initialCountries = [],
    initialStates = [],
    loadedData,
    setChartType,
    isUS = false,
  } = props;

  const [graphCountries, setGraphCountries] = useState(initialCountries);
  const [graphStates, setGraphStates] = useState(initialStates);
  const [dateCount, setDateCount] = useState(750);
  const [showSMA, setShowSMA] = useState(1);
  const [showBBands, setShowBBands] = useState(0);
  const [smaLength, setSmaLength] = useState(15);

  function handleCountryChange(e, index) {
    const val = e.target.value.toLowerCase();
    const curCountries = Array(...graphCountries);
    curCountries[index] = val === "Select" ? "" : val;

    setGraphCountries(curCountries);
  }

  function handleStateChange(e, index) {
    const val = e.target.value;
    const curStates = Array(...graphStates);
    curStates[index] = val === "Select" ? "" : val;

    setGraphStates(curStates);
  }

  function clearLoc(index) {
    if (isUS) {
      const curStates = Array(...graphStates);
      curStates[index] = "";
      setGraphStates(curStates);
    } else {
      const curCountries = Array(...graphCountries);
      curCountries[index] = "";
      setGraphCountries(curCountries);
    }
  }

  function convertToGraphData(data, countries) {
    const dateFields = Object.keys(data[0].timeline[`${chartType}`]).slice(
      -dateCount
    );

    const countriesData = data.filter((c) =>
      countries.includes(c.country.toLowerCase())
    );

    const output = dateFields.map((df, i) => {
      const pointData = { name: df };
      const countryData = countriesData.map((cd) => ({
        [`${cd.country.toLowerCase()}`]: cd.timeline[`${chartType}`][df],
      }));

      return Object.assign(pointData, ...countryData);
    });

    return output;
  }

  const convertToGraphDataUS = (data, states) => {
    const dateFields = Object.keys(data[0].timeline[`${chartType}`]).slice(
      -dateCount
    );

    const statesData = data.filter((c) =>
      states.includes(c.name.toLowerCase())
    );

    const output = dateFields.map((df, i) => {
      const pointData = { name: df };
      const stateData = statesData.map((sd) => ({
        [`${sd.name}`]: sd.timeline[`${chartType}`][df],
      }));

      return Object.assign(pointData, ...stateData);
    });

    return output;
  };

  function changeSmaVisibility(val) {
    const numVal = Number(val);

    setShowBBands(0);

    setShowSMA(numVal);
  }

  return (
    <StyledUI>
      <StyledGraphHolder>
        <MultiLineChart
          key="main"
          aspectRatio={0.6}
          data={
            isUS
              ? convertToGraphDataUS(loadedData, graphStates)
              : convertToGraphData(loadedData, graphCountries)
          }
          xField="name"
          yFields={isUS ? graphStates : graphCountries}
          scale="linear"
          smaLength={smaLength}
          showSMA={showSMA}
          showBBands={showBBands}
          tooltipFormatter={
            ["casesPer100k", "deathsPer100k"].includes(chartType)
              ? (y) => Number(y).toFixed(3)
              : (y) =>
                  Array.isArray(y)
                    ? `U: ${abbreviateNumber(y[0], 2)}, L: ${abbreviateNumber(
                        y[1],
                        2
                      )}`
                    : abbreviateNumber(y, 2)
          }
          yAxisLabel={
            chartType === "casesPer100k"
              ? "Cases Per 100k"
              : chartType === "activeCases"
              ? "Active Cases"
              : chartType === "newCases"
              ? "New Cases"
              : chartType === "newDeaths"
              ? "New Deaths"
              : chartType === "deathsPer100k"
              ? "Deaths Per 100k"
              : chartType
          }
          yTickFormatter={
            ["casesPer100k", "deathsPer100k"].includes(chartType)
              ? (y) => Number(y).toFixed(3)
              : (d) => abbreviateNumber(d, 2)
          }
        />
      </StyledGraphHolder>

      <StyledOptionsMenu>
        <h3 className="control-title">Graph Controls</h3>

        <div className="main-options">
          <div className="opt">
            <label htmlFor="dd_data">
              Data Type:
              <select
                onChange={(e) => setChartType(e.target.value)}
                className="custom-select"
                // style={{ maxWidth: "180px" }}
                defaultValue={chartType}
                id="dd_data"
              >
                <option value="cases">Total Cases</option>
                <option value="deaths">Deaths</option>
                <option value="newCases">New Cases</option>
                <option value="newDeaths">New Deaths</option>
                <option value="casesPer100k">Cases per 100k Pop.</option>
                <option value="deathsPer100k">Deaths per 100k Pop.</option>
              </select>
            </label>
          </div>

          <div className="opt">
            <label htmlFor="dd_days">
              Day Count:
              <select
                onChange={(e) => setDateCount(e.target.value)}
                className="custom-select"
                // style={{ maxWidth: "180px" }}
                defaultValue={dateCount}
                id="dd_days"
              >
                {[
                  750,
                  500,
                  400,
                  300,
                  150,
                  120,
                  100,
                  75,
                  50,
                  40,
                  30,
                  20,
                  10,
                  5,
                ].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="ta-controls">
          <h4>TA Controls</h4>
          <p>
            I recommend hiding all but one location when using Bollinger bands.
          </p>

          <div className="inner">
            <div className="opt">
              <label htmlFor="dd_sma">
                Show SMA
                <select
                  onChange={(e) => changeSmaVisibility(e.target.value)}
                  className="custom-select"
                  value={showSMA}
                  id="dd_sma"
                >
                  <option value={1}>Show</option>
                  <option value={0}>Hide</option>
                </select>
              </label>
            </div>

            <div className="opt">
              <label htmlFor="dd_smalength">
                SMA Length
                <select
                  onChange={(e) => setSmaLength(Number(e.target.value))}
                  className="custom-select"
                  value={smaLength}
                  id="dd_smalength"
                >
                  <option value={3}>3 days</option>
                  <option value={5}>5 days</option>
                  <option value={7}>7 days</option>
                  <option value={9}>9 days</option>
                  <option value={12}>12 days</option>
                  <option value={15}>15 days</option>
                  <option value={20}>20 days</option>
                </select>
              </label>
            </div>

            <div className="opt">
              <label htmlFor="dd_bbands">
                Bollinger Bands
                <select
                  disabled={showSMA !== 1}
                  onChange={(e) => setShowBBands(Number(e.target.value))}
                  className="custom-select"
                  value={showBBands}
                  id="dd_bbands"
                >
                  <option value={1}>Show</option>
                  <option value={0}>Hide</option>
                </select>
              </label>
            </div>
          </div>
        </div>

        <div className="loc-options">
          {isUS ? (
            <div>
              <p className="statesSelectLabel">Select States:</p>
              <div className="statesBox">
                {graphStates.map((state, i) => (
                  <div className="select-row" key={`dropdown_${i}`}>
                    <button type="button" onClick={() => clearLoc(i)}>
                      clear
                    </button>
                    <select
                      onChange={(e) => handleStateChange(e, i)}
                      className="custom-select mb-2 mx-1 mx-lg-0"
                      value={state}
                    >
                      <option value="">Select</option>
                      {loadedData.map((c, z) => (
                        <option
                          key={`sel-${i}-${z}`}
                          value={c.name.toLowerCase()}
                        >
                          {formatName(c.name)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <p className="fineprint">
                Note: You can select the US total at the bottom of each
                dropdown.
              </p>
            </div>
          ) : (
            <div>
              <p className="countriesSelectLabel">Select Countries:</p>
              <div className="countriesBox">
                {graphCountries.map((country, i) => (
                  <div className="select-row" key={`dropdown_${i}`}>
                    <button type="button" onClick={() => clearLoc(i)}>
                      clear
                    </button>
                    <select
                      onChange={(e) => handleCountryChange(e, i)}
                      className="custom-select mb-2 mx-1 mx-lg-0"
                      value={country}
                    >
                      <option value="">Select</option>
                      {loadedData.map((c, z) => (
                        <option
                          key={`sel-${i}-${z}`}
                          value={c.country.toLowerCase()}
                        >
                          {formatName(c.country)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <p className="fineprint">
                Note: You can select the world total at the bottom of each
                dropdown.
              </p>
            </div>
          )}
        </div>
      </StyledOptionsMenu>
    </StyledUI>
  );
};

export default CoronaGraphs;
